import type { browseListingStatus } from '../helpers/browseListingStatus';

export const MAP_MARKER_HEIGHT = 20;
const DEFAULT_MAP_MARKER_WIDTH = 40;
export const MAP_MARKER_POPUP_CLEARANCE_WIDTH = 88;

const IMAGE_PREFIX = 'marketplace-map-marker|';
const IMAGE_PADDING = 3;
const IMAGE_PIXEL_RATIO = 2;
const PRICE_FONT = '400 11px Arial, sans-serif';
const HEART_SLOT_WIDTH = 16;
const KEY_SLOT_WIDTH = 15;

export const MAP_MARKER_COLORS = {
  listed: '#143d73',
  selfTour: '#0e6b57',
  comingSoon: '#73429b',
  comingSoonText: '#613483',
  selected: '#101c2c',
  focus: '#0042e6',
  white: '#fff',
} as const;

const HEART_PATH =
  'M6.979 3.074a6 6 0 0 1 4.988 1.425l.037 .033l.034 -.03a6 6 0 0 1 4.733 -1.44l.246 .036a6 6 0 0 1 3.364 10.008l-.18 .185l-.048 .041l-7.45 7.379a1 1 0 0 1 -1.313 .082l-.094 -.082l-7.493 -7.422a6 6 0 0 1 3.176 -10.215z';

export type MapMarkerVariant = NonNullable<ReturnType<typeof browseListingStatus>>;

const MARKER_BACKGROUND_COLORS = {
  listed: MAP_MARKER_COLORS.listed,
  'self-tour': MAP_MARKER_COLORS.selfTour,
  'coming-soon': MAP_MARKER_COLORS.white,
} satisfies Record<MapMarkerVariant, string>;

export type MapMarkerImageConfig = {
  hasFocusRing: boolean;
  isHighlighted: boolean;
  isLiked: boolean;
  isLoading: boolean;
  label: string;
  variant: MapMarkerVariant;
};

export type MarkerImageMap = {
  addImage: (id: string, image: ImageData, options: { pixelRatio: number }) => unknown;
  hasImage: (id: string) => boolean;
  removeImage: (id: string) => unknown;
};

const registeredMarkerImageIds = new WeakMap<object, Set<string>>();
const registeredMarkerImageWidths = new WeakMap<object, Map<string, number>>();

export function mapMarkerImageId(config: MapMarkerImageConfig) {
  return `${IMAGE_PREFIX}${Number(config.hasFocusRing)}|${Number(config.isHighlighted)}|${Number(
    config.isLiked,
  )}|${Number(config.isLoading)}|${config.variant}|${encodeURIComponent(config.label)}`;
}

type RoundedRectangleOptions = {
  context: CanvasRenderingContext2D;
  height: number;
  radius: number;
  width: number;
  x: number;
  y: number;
};

function roundedRectangle({ context, height, radius, width, x, y }: RoundedRectangleOptions) {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.arcTo(x + width, y, x + width, y + radius, radius);
  context.lineTo(x + width, y + height - radius);
  context.arcTo(x + width, y + height, x + width - radius, y + height, radius);
  context.lineTo(x + radius, y + height);
  context.arcTo(x, y + height, x, y + height - radius, radius);
  context.lineTo(x, y + radius);
  context.arcTo(x, y, x + radius, y, radius);
  context.closePath();
}

function parseMapMarkerImageId(id: string): MapMarkerImageConfig | null {
  if (!id.startsWith(IMAGE_PREFIX)) {
    return null;
  }
  const [focused, highlighted, liked, loading, variant, encodedLabel] = id
    .slice(IMAGE_PREFIX.length)
    .split('|');
  if (
    (focused !== '0' && focused !== '1') ||
    (highlighted !== '0' && highlighted !== '1') ||
    (liked !== '0' && liked !== '1') ||
    (loading !== '0' && loading !== '1') ||
    (variant !== 'listed' && variant !== 'self-tour' && variant !== 'coming-soon') ||
    encodedLabel == null
  ) {
    return null;
  }
  try {
    return {
      hasFocusRing: focused === '1',
      isHighlighted: highlighted === '1',
      isLiked: liked === '1',
      isLoading: loading === '1',
      label: decodeURIComponent(encodedLabel),
      variant,
    };
  } catch {
    return null;
  }
}

type DrawIconOptions = {
  context: CanvasRenderingContext2D;
  x: number;
  y: number;
};

function drawHeart({ context, x, y }: DrawIconOptions) {
  const path = new Path2D(HEART_PATH);
  context.save();
  context.translate(x, y);
  context.scale(12 / 24, 12 / 24);
  // oxlint-disable-next-line unicorn/no-array-fill-with-reference-type -- Canvas fill is not Array.fill.
  context.fill(path);
  context.restore();
}

function drawKey({ context, x, y }: DrawIconOptions) {
  context.strokeStyle = context.fillStyle;
  context.lineWidth = 1.5;
  context.beginPath();
  context.arc(x + 3, y, 2.7, 0, Math.PI * 2);
  context.stroke();
  context.beginPath();
  context.moveTo(x + 5.7, y);
  context.lineTo(x + 13, y);
  context.lineTo(x + 13, y + 3);
  context.moveTo(x + 10, y);
  context.lineTo(x + 10, y + 2);
  context.stroke();
}

function createMapMarkerImage(config: MapMarkerImageConfig) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return null;
  }
  context.font = PRICE_FONT;
  const textWidth = Math.ceil(context.measureText(config.label).width);
  const keyWidth = config.variant === 'self-tour' ? KEY_SLOT_WIDTH : 0;
  const heartWidth = config.isLiked && !config.isLoading ? HEART_SLOT_WIDTH : 0;
  const width = Math.max(DEFAULT_MAP_MARKER_WIDTH, textWidth + 12) + keyWidth + heartWidth;
  canvas.width = (width + IMAGE_PADDING * 2) * IMAGE_PIXEL_RATIO;
  canvas.height = (MAP_MARKER_HEIGHT + IMAGE_PADDING * 2) * IMAGE_PIXEL_RATIO;
  context.scale(IMAGE_PIXEL_RATIO, IMAGE_PIXEL_RATIO);

  const isComingSoon = config.variant === 'coming-soon';
  context.fillStyle = config.isHighlighted
    ? MAP_MARKER_COLORS.selected
    : MARKER_BACKGROUND_COLORS[config.variant];
  roundedRectangle({
    context,
    height: MAP_MARKER_HEIGHT - 1.4,
    radius: (MAP_MARKER_HEIGHT - 1.4) / 2,
    width: width - 1.4,
    x: IMAGE_PADDING + 0.7,
    y: IMAGE_PADDING + 0.7,
  });
  context.fill();

  if (config.isHighlighted || config.hasFocusRing) {
    context.lineWidth = config.hasFocusRing ? 5 : 4;
    context.strokeStyle = MAP_MARKER_COLORS.focus;
    context.stroke();
  }
  context.strokeStyle = isComingSoon ? MAP_MARKER_COLORS.comingSoon : MAP_MARKER_COLORS.white;
  context.lineWidth = isComingSoon ? 2 : 1.3;
  context.setLineDash(isComingSoon ? [3, 2] : []);
  context.stroke();
  context.setLineDash([]);

  context.font = PRICE_FONT;
  context.textBaseline = 'middle';
  context.textAlign = 'left';
  context.fillStyle =
    isComingSoon && !config.isHighlighted
      ? MAP_MARKER_COLORS.comingSoonText
      : MAP_MARKER_COLORS.white;
  const centerY = IMAGE_PADDING + MAP_MARKER_HEIGHT / 2 + 0.3;
  let contentX = IMAGE_PADDING + (width - textWidth - keyWidth - heartWidth) / 2;
  if (heartWidth) {
    drawHeart({ context, x: contentX, y: centerY - 6 });
    contentX += heartWidth;
  }
  if (keyWidth) {
    drawKey({ context, x: contentX, y: centerY });
    contentX += keyWidth;
  }
  context.fillText(config.label, contentX, centerY);
  return context.getImageData(0, 0, canvas.width, canvas.height);
}

export function getRegisteredMapMarkerImageIds(map: MarkerImageMap) {
  let imageIds = registeredMarkerImageIds.get(map);
  if (!imageIds) {
    imageIds = new Set();
    registeredMarkerImageIds.set(map, imageIds);
  }
  return imageIds;
}

export function getMapMarkerImageWidth({ id, map }: { id: string; map: object }) {
  return registeredMarkerImageWidths.get(map)?.get(id);
}

type MarkerImagesOptions = {
  imageIds: Iterable<string>;
  map: MarkerImageMap;
};

type RegisterMapMarkerImageOptions = {
  id: string;
  map: MarkerImageMap;
};

export function registerMapMarkerImage({ id, map }: RegisterMapMarkerImageOptions) {
  if (map.hasImage(id) && getMapMarkerImageWidth({ id, map }) != null) {
    getRegisteredMapMarkerImageIds(map).add(id);
    return;
  }
  const imageConfig = parseMapMarkerImageId(id);
  if (!imageConfig) {
    return;
  }
  const image = createMapMarkerImage(imageConfig);
  if (image) {
    if (!map.hasImage(id)) {
      map.addImage(id, image, { pixelRatio: IMAGE_PIXEL_RATIO });
    }
    let widths = registeredMarkerImageWidths.get(map);
    if (!widths) {
      widths = new Map();
      registeredMarkerImageWidths.set(map, widths);
    }
    widths.set(id, image.width / IMAGE_PIXEL_RATIO - IMAGE_PADDING * 2);
    getRegisteredMapMarkerImageIds(map).add(id);
  }
}

export function removeMapMarkerImage({ id, map }: RegisterMapMarkerImageOptions) {
  if (map.hasImage(id)) {
    map.removeImage(id);
  }
  registeredMarkerImageIds.get(map)?.delete(id);
  registeredMarkerImageWidths.get(map)?.delete(id);
}

export function registerMapMarkerImages({ imageIds, map }: MarkerImagesOptions) {
  for (const id of imageIds) {
    registerMapMarkerImage({ id, map });
  }
}
